import {
  GatewayDeclarationActivite,
  GatewayQuestionnaire,
} from "@conformite/gateway";
import {
  BannerCard,
  InfoCard,
  LoadingPage,
} from "@endya-conformite/ui-kit/src/components";
import {
  FormProvider,
  useForm,
  useWatch,
} from "@endya-conformite/ui-kit/src/lya/react-hook-form";
import {
  Button,
  displayErrorToast,
  displaySucessToast,
  FormGrid,
} from "@endya-conformite/ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AiAnswer,
  QuestionnaireQuestion,
} from "../QuestionnaireQuestion/QuestionnaireQuestion";
import styles from "./QuestionnaireForm.module.scss";
import { computeOptionColorOverrides } from "../conditionalColors";
import {
  computeVisibleQuestionIds,
  useVisibleQuestionIds,
} from "./useQuestionConditionEvaluator";
import {
  QuestionnairePrefill,
  useQuestionnaireForm,
} from "./useQuestionnaireForm";
import { useSharedAnswerSync } from "./useSharedAnswerSync";

function buildAnswersSignature(
  questions: GatewayQuestionnaire.QuestionDefinitionResponse[],
  values: Record<string, string>,
  comment: string
): string {
  const visibleIds = computeVisibleQuestionIds(questions, values);
  const answers = questions
    .map((q) => q.id)
    .filter((id) => visibleIds.has(id) && Boolean(values[id]))
    .sort()
    .map((id) => `${id}=${values[id]}`);
  return JSON.stringify({ answers, comment: comment || "" });
}

type QuestionnaireFormProps = {
  slug: GatewayQuestionnaire.QuestionnaireSlug | undefined;
  contextType: string;
  contextId: string;
  title?: string;
  disabled?: boolean;
  onSaved?: () => void;
  onAnswersChange?: (answers: Record<string, string>) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  hideHeader?: boolean;
  aiAnswersByQuestionId?: Map<string, AiAnswer>;
  prefill?: QuestionnairePrefill[];
  isPrefillLoading?: boolean;
  declaredIobspActivites?: GatewayDeclarationActivite.ActiviteIobsp[];
};

function QuestionnaireFormContent({
  slug,
  questions,
  disabled,
  visibleIdsRef,
  aiAnswersByQuestionId,
  onAnswersChange,
  declaredIobspActivites,
}: {
  slug: GatewayQuestionnaire.QuestionnaireSlug | undefined;
  questions: GatewayQuestionnaire.QuestionDefinitionResponse[];
  disabled?: boolean;
  visibleIdsRef: React.MutableRefObject<Set<string>>;
  aiAnswersByQuestionId?: Map<string, AiAnswer>;
  onAnswersChange?: (answers: Record<string, string>) => void;
  declaredIobspActivites?: GatewayDeclarationActivite.ActiviteIobsp[];
}) {
  const visibleQuestionIds = useVisibleQuestionIds(questions);
  const answers = useWatch() as Record<string, string>;

  visibleIdsRef.current = visibleQuestionIds;

  useEffect(() => {
    onAnswersChange?.(answers);
  }, [answers, onAnswersChange]);

  return (
    <FormGrid disableHorizontalGutter>
      {questions.map((question) => {
        if (!visibleQuestionIds.has(question.id)) return null;
        return (
          <QuestionnaireQuestion
            key={question.id}
            question={question}
            disabled={disabled}
            aiAnswer={aiAnswersByQuestionId?.get(question.id)}
            colorOverrides={computeOptionColorOverrides(
              slug,
              question,
              questions,
              answers,
              declaredIobspActivites
            )}
          />
        );
      })}
    </FormGrid>
  );
}

export function QuestionnaireForm({
  slug,
  contextType,
  contextId,
  title,
  disabled,
  onSaved,
  onAnswersChange,
  onDirtyChange,
  hideHeader,
  aiAnswersByQuestionId,
  prefill,
  isPrefillLoading,
  declaredIobspActivites,
}: QuestionnaireFormProps) {
  const {
    definition,
    answers,
    save,
    isLoading,
    isSaving,
    comment: initialComment,
    persistedAiAnswers,
    hasSavedInstance,
  } = useQuestionnaireForm(slug, contextType, contextId, {
    prefill,
    isPrefillLoading,
  });

  const formMethods = useForm<Record<string, string>>({ defaultValues: {} });
  const { reset, getValues, setValue, watch, control } = formMethods;
  const watchedValues = useWatch({ control }) as Record<string, string>;
  const visibleIdsRef = useRef<Set<string>>(new Set());
  const hasResetRef = useRef(false);
  const deselectedSharedQsRef = useRef<Set<string>>(new Set());
  const [isFormReady, setIsFormReady] = useState(false);
  const [savedSignature, setSavedSignature] = useState<string | null>(null);

  const sortedQuestions = useMemo(
    () =>
      definition
        ? [...definition.questions].sort((a, b) => a.order - b.order)
        : [],
    [definition]
  );

  const currentSignature = useMemo(
    () =>
      buildAnswersSignature(
        sortedQuestions,
        watchedValues ?? {},
        watchedValues?.comment ?? ""
      ),
    [sortedQuestions, watchedValues]
  );
  const hasUnsavedChanges =
    savedSignature !== null && currentSignature !== savedSignature;

  const { publishSharedFromSave } = useSharedAnswerSync({
    slug,
    questions: sortedQuestions,
    isReady: isFormReady,
    watch,
    getValues,
    setValue,
    deselectedSharedQsRef,
  });

  const effectiveAiAnswers = useMemo(() => {
    const merged = new Map<string, AiAnswer>(persistedAiAnswers);
    aiAnswersByQuestionId?.forEach((v, k) => merged.set(k, v));
    return merged;
  }, [persistedAiAnswers, aiAnswersByQuestionId]);

  const areAiAnswersStale = useMemo(() => {
    if (!effectiveAiAnswers || effectiveAiAnswers.size === 0) return false;
    return !sortedQuestions.some((q) => effectiveAiAnswers.has(q.id));
  }, [effectiveAiAnswers, sortedQuestions]);

  // Initialise le formulaire react-hook-form avec les réponses chargées (une seule fois)
  useEffect(() => {
    if (isLoading || hasResetRef.current) return;
    reset({ ...answers, comment: initialComment });
    hasResetRef.current = true;
    setIsFormReady(true);
    setSavedSignature(
      buildAnswersSignature(sortedQuestions, answers, initialComment)
    );
    if (hasSavedInstance) {
      const answeredIds = new Set(Object.keys(answers));
      deselectedSharedQsRef.current = new Set(
        sortedQuestions.map((q) => q.id).filter((id) => !answeredIds.has(id))
      );
    }
  }, [isLoading, answers, initialComment, reset, sortedQuestions, hasSavedInstance]);

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  if (isLoading || !definition) return <LoadingPage withText={false} />;

  const handleSave = async () => {
    const visibleQuestions = sortedQuestions.filter((q) =>
      visibleIdsRef.current.has(q.id)
    );
    const visibleFieldNames = visibleQuestions.map((q) => q.id);
    // On autorise l'enregistrement d'une grille partiellement remplie : le
    // vérificateur ne peut pas toujours tout compléter en une seule fois.
    // La complétude reste contrôlée en amont du bouton « Valider l'axe ».
    try {
      const allValues = getValues();
      const visibleAnswers: Record<string, string> = {};
      visibleFieldNames.forEach((name) => {
        if (allValues[name]) visibleAnswers[name] = allValues[name];
      });
      const { comment } = allValues;
      await save(visibleAnswers, comment, effectiveAiAnswers);
      deselectedSharedQsRef.current = new Set(
        visibleFieldNames.filter((name) => !visibleAnswers[name])
      );
      publishSharedFromSave(visibleAnswers);
      reset({ ...visibleAnswers, comment: comment ?? "" });
      setSavedSignature(
        buildAnswersSignature(sortedQuestions, visibleAnswers, comment ?? "")
      );
      displaySucessToast("Questionnaire enregistré avec succès");
      onSaved?.();
    } catch {
      displayErrorToast("Une erreur est survenue lors de l'enregistrement");
    }
  };

  const formContent = (
    <>
      {areAiAnswersStale && (
        <InfoCard type="warning" className={styles.aiStaleNotice}>
          Indications IA obsolètes : le questionnaire a été modifié depuis
          l&apos;analyse.
        </InfoCard>
      )}
      <FormProvider {...formMethods}>
        <QuestionnaireFormContent
          slug={slug}
          questions={sortedQuestions}
          disabled={disabled}
          visibleIdsRef={visibleIdsRef}
          aiAnswersByQuestionId={effectiveAiAnswers}
          onAnswersChange={onAnswersChange}
          declaredIobspActivites={declaredIobspActivites}
        />
      </FormProvider>
    </>
  );

  const footer = !disabled ? (
    <div className={styles.actions}>
      <Button
        variant="filled"
        onClick={() => {
          void handleSave();
        }}
        disabled={isSaving || !hasUnsavedChanges}
      >
        {isSaving ? "Enregistrement..." : "Enregistrer"}
      </Button>
    </div>
  ) : undefined;

  if (hideHeader) {
    return (
      <div>
        <div className={styles.body}>{formContent}</div>
        {footer}
      </div>
    );
  }

  return (
    <BannerCard title={title ?? definition.name} footer={footer}>
      {formContent}
    </BannerCard>
  );
}
